import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { fetchAccounts } from '../../api/api';
import { useTrade } from '../../context/TradeProvider';
import { useUserPreferences } from '../../context/UserPreferencesContext';
import { useAccountData } from '../../context/AccountDataProvider';
import {
  accountIdsEqual,
  normalizeAccountList,
  orderAccountsByPreferredDefault,
  resolveInitialAccountId,
} from '../../utils/accountSelection';

/**
 * AccountBalanceWidget - Compact widget showing account balance, equity, and P/L.
 * Live values come from the shared AccountDataProvider connection.
 */
export default function AccountBalanceWidget({ accountId: propAccountId, restrictToAccount = false }) {
  const { accountId: contextAccountId } = useTrade();
  const { defaultAccountId, loaded: preferencesLoaded = true } = useUserPreferences() || {};
  const accountContext = useAccountData() || {};
  const {
    connect,
    isConnected: sharedIsConnected,
    connectedAccountId,
    balance,
    equity,
    margin,
    freeMargin,
    marginLevel,
    floatingPnl,
    currency: sharedCurrency,
  } = accountContext;
  
  // data
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [accountData, setAccountData] = useState({
    balance: null,
    equity: null,
    profit: null,
    margin: null,
    freeMargin: null,
    marginLevel: null,
    currency: 'USD',
  });
  
  // ui state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  
  // Load accounts list
  const loadAccounts = useCallback(async () => {
    try {
      const resp = await fetchAccounts();
      const arr = normalizeAccountList(resp);
      const ordered = orderAccountsByPreferredDefault(arr, defaultAccountId);
      setAccounts(ordered);
      
      // Set initial selected account
      const targetId = propAccountId || contextAccountId;
      if (targetId && ordered.some(a => accountIdsEqual(a.id, targetId))) {
        setSelectedAccountId(targetId);
      } else if (ordered.length > 0 && preferencesLoaded) {
        setSelectedAccountId(resolveInitialAccountId(ordered, null, defaultAccountId));
      }
    } catch (e) {
      console.error('AccountBalanceWidget: fetchAccounts failed', e);
      setError('Failed to load accounts');
    }
  }, [propAccountId, contextAccountId, defaultAccountId, preferencesLoaded]);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Sync with context account when it changes
  useEffect(() => {
    if (!restrictToAccount && contextAccountId && accounts.some(a => accountIdsEqual(a.id, contextAccountId))) {
      setSelectedAccountId(contextAccountId);
    }
  }, [contextAccountId, accounts, restrictToAccount]);

  // Use the shared account connection instead of opening a widget-local socket.
  useEffect(() => {
    if (!selectedAccountId) return;

    setLoading(true);
    setError(null);

    connect?.(selectedAccountId, { primeSnapshot: true, resetOnNoCache: false });

    return undefined;
  }, [selectedAccountId, connect]);

  useEffect(() => {
    if (!selectedAccountId) return;
    const selectedMatchesShared = !connectedAccountId || accountIdsEqual(connectedAccountId, selectedAccountId);
    if (!selectedMatchesShared) return;

    const acc = accounts.find(a => accountIdsEqual(a.id, selectedAccountId));
    setAccountData({
      balance: balance ?? acc?.balance ?? null,
      equity: equity ?? acc?.equity ?? null,
      profit: floatingPnl ?? acc?.profit ?? null,
      margin: margin ?? acc?.margin ?? acc?.used_margin ?? null,
      freeMargin: freeMargin ?? acc?.free_margin ?? null,
      marginLevel: marginLevel ?? acc?.margin_level ?? null,
      currency: sharedCurrency || acc?.currency || 'USD',
    });
    setIsConnected(Boolean(sharedIsConnected && selectedMatchesShared));
    setLoading(false);
  }, [
    selectedAccountId,
    connectedAccountId,
    accounts,
    balance,
    equity,
    floatingPnl,
    margin,
    freeMargin,
    marginLevel,
    sharedCurrency,
    sharedIsConnected,
  ]);

  // Format currency
  const formatCurrency = (value, currency = 'USD') => {
    if (value == null) return '—';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `$${Number(value).toFixed(2)}`;
    }
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value == null) return '—';
    return `${Number(value).toFixed(2)}%`;
  };

  // Calculate P/L percentage
  const plPercent = accountData.balance && accountData.profit 
    ? (accountData.profit / accountData.balance) * 100 
    : null;

  const selectedAccount = accounts.find(a => a.id === selectedAccountId);
  const currency = accountData.currency || selectedAccount?.currency || 'USD';

  return (
    <Card className="h-full bg-transparent border-0">
      {/* Header with account selector */}
      <div className="px-2 pt-1 pb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {!restrictToAccount && accounts.length > 1 && (
            <select
              className="text-xs bg-[rgb(28,33,37)] border border-white/10 rounded px-2 py-1 text-white/90 max-w-[140px] truncate"
              value={selectedAccountId || ''}
              onChange={(e) => setSelectedAccountId(e.target.value)}
            >
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          )}
          {(restrictToAccount || accounts.length <= 1) && selectedAccount && (
            <span className="text-xs text-white/70 truncate">{selectedAccount.name}</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-emerald-400' : 'bg-gray-500'}`} title={isConnected ? 'Live' : 'Disconnected'} />
          <span className="text-[10px] text-white/50">{isConnected ? 'Live' : 'Offline'}</span>
        </div>
      </div>

      <CardContent className="px-2 py-2 space-y-2">
        {error && <div className="text-xs text-rose-400">{error}</div>}
        
        {loading && !accountData.balance && (
          <div className="text-xs text-gray-400">Loading…</div>
        )}

        {/* Main metrics */}
        {(accountData.balance != null || accountData.equity != null) && (
          <div className="space-y-2">
            {/* Balance & Equity row */}
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-white/5 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                <div className="text-[10px] sm:text-xs text-white/60">Balance</div>
                <div className="text-sm sm:text-base font-semibold text-white/90 truncate">
                  {formatCurrency(accountData.balance, currency)}
                </div>
              </div>
              <div className="bg-white/5 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
                <div className="text-[10px] sm:text-xs text-white/60">Equity</div>
                <div className="text-sm sm:text-base font-semibold text-white/90 truncate">
                  {formatCurrency(accountData.equity, currency)}
                </div>
              </div>
            </div>

            {/* P/L row */}
            <div className="bg-white/5 rounded-lg px-2 py-1.5 sm:px-3 sm:py-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] sm:text-xs text-white/60">Unrealized P/L</div>
                  <div className={`text-sm sm:text-lg font-semibold ${
                    accountData.profit == null ? 'text-white/90' :
                    accountData.profit >= 0 ? 'text-emerald-400' : 'text-rose-400'
                  }`}>
                    {accountData.profit != null && accountData.profit >= 0 ? '+' : ''}
                    {formatCurrency(accountData.profit, currency)}
                  </div>
                </div>
                {plPercent != null && (
                  <div className={`text-xs sm:text-sm font-medium px-2 py-0.5 rounded ${
                    plPercent >= 0 ? 'bg-emerald-400/20 text-emerald-400' : 'bg-rose-400/20 text-rose-400'
                  }`}>
                    {plPercent >= 0 ? '+' : ''}{plPercent.toFixed(2)}%
                  </div>
                )}
              </div>
            </div>

            {/* Margin info (collapsible on mobile) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[10px] sm:text-xs">
              <div className="bg-white/5 rounded px-2 py-1.5">
                <div className="text-white/50">Margin</div>
                <div className="text-white/80 font-medium truncate">
                  {formatCurrency(accountData.margin, currency)}
                </div>
              </div>
              <div className="bg-white/5 rounded px-2 py-1.5">
                <div className="text-white/50">Free Margin</div>
                <div className="text-white/80 font-medium truncate">
                  {formatCurrency(accountData.freeMargin, currency)}
                </div>
              </div>
              <div className="bg-white/5 rounded px-2 py-1.5 col-span-2 sm:col-span-1">
                <div className="text-white/50">Margin Level</div>
                <div className={`font-medium ${
                  accountData.marginLevel == null ? 'text-white/80' :
                  accountData.marginLevel > 200 ? 'text-emerald-400' :
                  accountData.marginLevel > 100 ? 'text-yellow-400' : 'text-rose-400'
                }`}>
                  {formatPercent(accountData.marginLevel)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && !accountData.balance && !error && (
          <div className="text-xs text-gray-400 text-center py-4">
            Select an account to view balance
          </div>
        )}
      </CardContent>
    </Card>
  );
}
